import { useState } from 'react';
import { Box, Typography, Button, Alert, Collapse, LinearProgress } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { UpgradeModal } from './UpgradeModal';
import { useTierAccess, TierLimits } from '../hooks/useTierAccess';

type LimitResource = 'products' | 'clients' | 'locations' | 'stores' | 'warehouses' | 'staff';

interface LimitWarningBannerProps {
  resource: LimitResource;
  showProgress?: boolean;
  dismissible?: boolean;
  variant?: 'inline' | 'banner';
}

const RESOURCE_LABELS: Record<LimitResource, string> = {
  products: 'products',
  clients: 'clients',
  locations: 'locations',
  stores: 'stores',
  warehouses: 'warehouses',
  staff: 'staff members',
};

const RESOURCE_MAX_KEYS: Record<LimitResource, keyof TierLimits> = {
  products: 'maxProducts',
  clients: 'maxClients',
  locations: 'maxLocations',
  stores: 'includedStores',
  warehouses: 'includedWarehouses',
  staff: 'includedStaff',
};

const RESOURCE_CURRENT_KEYS: Record<LimitResource, keyof TierLimits> = {
  products: 'currentProducts',
  clients: 'currentClients',
  locations: 'currentLocations',
  stores: 'currentStores',
  warehouses: 'currentWarehouses',
  staff: 'currentStaff',
};

/**
 * Banner component that shows warnings when approaching resource limits
 * and errors when limits are reached.
 *
 * Usage:
 * ```tsx
 * <LimitWarningBanner resource="products" showProgress />
 * ```
 */
export const LimitWarningBanner = ({
  resource,
  showProgress = false,
  dismissible = true,
  variant = 'banner',
}: LimitWarningBannerProps) => {
  const { tier, limits, isUnlimited } = useTierAccess();
  const [dismissed, setDismissed] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Don't show for unlimited resources
  if (isUnlimited(RESOURCE_MAX_KEYS[resource]) || !limits) {
    return null;
  }

  const max = limits[RESOURCE_MAX_KEYS[resource]] as number;
  const current = limits[RESOURCE_CURRENT_KEYS[resource]] as number;
  const remaining = max - current;
  const percentage = (current / max) * 100;
  const label = RESOURCE_LABELS[resource];

  // Determine severity
  const isAtLimit = remaining <= 0;
  const isWarning = remaining <= 10 && remaining > 0;

  // Don't show if not at warning threshold
  if (!isAtLimit && !isWarning) {
    return null;
  }

  // Don't show if dismissed
  if (dismissed) {
    return null;
  }

  const severity = isAtLimit ? 'error' : 'warning';
  const Icon = isAtLimit ? ErrorOutlineIcon : WarningAmberIcon;

  const message = isAtLimit
    ? `You've reached your ${label} limit (${max}). Upgrade to add more.`
    : `You're approaching your ${label} limit. ${remaining} remaining of ${max}.`;

  if (variant === 'inline') {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: isAtLimit ? 'error.light' : 'warning.light',
            color: isAtLimit ? 'error.dark' : 'warning.dark',
          }}
        >
          <Icon fontSize="small" />
          <Typography variant="caption" sx={{ flex: 1 }}>
            {remaining <= 0 ? 'Limit reached' : `${remaining} remaining`}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => setUpgradeModalOpen(true)}
            sx={{ minWidth: 'auto', p: 0.5 }}
          >
            Upgrade
          </Button>
        </Box>

        <UpgradeModal
          open={upgradeModalOpen}
          onClose={() => setUpgradeModalOpen(false)}
          feature={`More ${label}`}
          currentTier={tier}
        />
      </>
    );
  }

  return (
    <>
      <Collapse in={!dismissed}>
        <Alert
          severity={severity}
          icon={<Icon />}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                color="inherit"
                startIcon={<RocketLaunchIcon />}
                onClick={() => setUpgradeModalOpen(true)}
              >
                Upgrade
              </Button>
              {dismissible && (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setDismissed(true)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          }
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">{message}</Typography>
          {showProgress && (
            <Box sx={{ mt: 1, width: '100%', maxWidth: 300 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(percentage, 100)}
                color={severity}
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {current} / {max} {label} used
              </Typography>
            </Box>
          )}
        </Alert>
      </Collapse>

      <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        feature={`More ${label}`}
        currentTier={tier}
      />
    </>
  );
};

export default LimitWarningBanner;
